import { Box, Button, CircularProgress, Typography } from "@mui/material";
import clsx from "clsx";
import { Link, useNavigate } from "react-router-dom";

import {
  DEAD_ACCOUNT_REBIRTH_PATH,
  PLAYMAFIA_CREATE_ACCOUNT_URL,
} from "@/constants/deadAccount";
import { KillHistoryResultType } from "@/constants/killHistory";
import { getKillHistoryResultLabel } from "@/helper/deadAccountKiller";
import { useDeadAccountKiller } from "@/hooks/useDeadAccountKiller";
import { formatDate, shortAddress } from "@/utils/utils";
import useStyles from "./index.styles";

interface DeadAccountScreenProps {
  onRebirthClick?: () => void;
  onUnstakeClick?: () => void;
  onCreateAccountClick?: () => void;
  showActions?: boolean;
  /** fullscreen = viewport gate in MainLayout; embedded = section on /rebirth */
  variant?: "fullscreen" | "embedded";
}

const DeadAccountScreen = ({
  onRebirthClick,
  onUnstakeClick,
  onCreateAccountClick,
  showActions = true,
  variant = "embedded",
}: DeadAccountScreenProps) => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const { killerInfo, isLoading: isLoadingKiller } = useDeadAccountKiller();

  const renderKillerName = () => {
    if (!killerInfo) {
      return null;
    }

    const hasProfileName =
      killerInfo.killerName &&
      !killerInfo.killerName.startsWith("0x");

    if (hasProfileName) {
      return (
        <Link to={`/profile/${killerInfo.killerName}`} className={classes.killerLink}>
          {killerInfo.killerName}
        </Link>
      );
    }

    return (
      <Typography className={classes.killerValue}>
        {killerInfo.killerAddress
          ? shortAddress(killerInfo.killerAddress)
          : killerInfo.killerName}
      </Typography>
    );
  };

  const renderResultClass = (resultType: KillHistoryResultType) => {
    if (resultType === KillHistoryResultType.DoubleKill) {
      return classes.killerResultDouble;
    }

    if (resultType === KillHistoryResultType.BackfireKill) {
      return classes.killerResultBackfire;
    }

    return classes.killerResultSingle;
  };

  const handleRebirth = () => {
    if (onRebirthClick) {
      onRebirthClick();
    } else {
      navigate(DEAD_ACCOUNT_REBIRTH_PATH);
    }
  };

  const handleUnstake = () => {
    if (onUnstakeClick) {
      onUnstakeClick();
    } else {
      navigate("/rank-activation");
    }
  };

  const handleCreateAccount = () => {
    if (onCreateAccountClick) {
      onCreateAccountClick();
    } else {
      window.location.href = PLAYMAFIA_CREATE_ACCOUNT_URL;
    }
  };

  return (
    <Box
      className={cx(
        classes.screen,
        variant === "fullscreen" && classes.screenFullscreen
      )}
    >
      <Box
        component="img"
        src="/assets/imgs/dead/skull.png"
        alt="Dead"
        className={classes.icon}
      />

      <Typography className={classes.title}>You are dead</Typography>

      <Typography className={classes.subtitle}>
        Your account has been eliminated. Your attacker claimed your assets.
        You can pay the vault to rebirth with penalties, or unstake your
        remaining rank and equipment stakes.
      </Typography>

      {isLoadingKiller && (
        <Box sx={{ marginBottom: 3 }}>
          <CircularProgress size={28} sx={{ color: "#c44b4b" }} />
        </Box>
      )}

      {!isLoadingKiller && killerInfo && (
        <Box className={classes.killerCard}>
          <Typography className={classes.killerCardTitle}>
            Killer information
          </Typography>

          <Box className={classes.killerRow}>
            <Typography className={classes.killerLabel}>Killer</Typography>
            {renderKillerName()}
          </Box>

          <Box className={classes.killerRow}>
            <Typography className={classes.killerLabel}>Timestamp</Typography>
            <Typography className={classes.killerValue}>
              {killerInfo.timestamp > 0
                ? formatDate(killerInfo.timestamp)
                : "—"}
            </Typography>
          </Box>

          <Box className={classes.killerRow}>
            <Typography className={classes.killerLabel}>Result</Typography>
            <Typography
              className={clsx(
                classes.killerValue,
                renderResultClass(killerInfo.resultType)
              )}
            >
              {getKillHistoryResultLabel(killerInfo.resultType)}
            </Typography>
          </Box>

          {killerInfo.killerWords && (
            <Box className={classes.killerMessage}>
              <Typography className={classes.killerMessageLabel}>
                Message from your killer
              </Typography>
              <Typography className={classes.killerMessageText}>
                “{killerInfo.killerWords}”
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {showActions && (
        <Box className={classes.actions}>
          <Button className={classes.primaryButton} onClick={handleRebirth}>
            Go to rebirth page
          </Button>
          <Button className={classes.secondaryButton} onClick={handleUnstake}>
            Unstake assets
          </Button>
          <Button
            className={classes.tertiaryButton}
            onClick={handleCreateAccount}
          >
            Create new account
          </Button>
        </Box>
      )}

      <Typography className={classes.restrictionNote}>
        Dead accounts cannot trade, commit crimes, or use ecosystem contracts
        until reborn. Only rebirth and unstaking are available.
      </Typography>
    </Box>
  );
};

export default DeadAccountScreen;
